import type {
  IPickPageCampaignTarget,
  IPickPageOtherCampaignTargets,
  IPickPageCurrentCampaignUsage,
  IPickPageRemoveConfirmState,
  IPickPageItem,
} from '../types';
import type { CampaignVariantTargetsQueryResponse } from '~/api/graphql/queries/campaignVariantTargets.generated';
import type { Campaign, Variant } from '~/modules/campaign/types';

import {
  isPickPageOnlineStoreAvailable,
  type IIsPickPageOnlineStoreAvailableParams,
} from './pickPageItem';
import { PICK_PAGE_HOME_ID, PICK_PAGE_HOME_PATH } from '../constants';
import { getShopifyPageId } from '~/modules/page-analytic/helpers';
import { ICampaignType } from '~/modules/campaign/types';
import { CAMPAIGN_TYPE } from '~/constants/campaign';
import { toPickPageMatchPath } from './pickPageItem';
import { TemplateType } from '~/types/template';

type ICampaignVariantTarget = CampaignVariantTargetsQueryResponse['campaignVariantTargets'][number];

type IVariantTarget = ICampaignVariantTarget['variants'][number];

/**
 * How a page is identified wherever one has to be matched against another — a stored variant against
 * a listed row, or against the other side of the same experiment: the record it points at. The
 * homepage is the one page with none — it is stored as `INDEX` with an empty baseID — so it answers to
 * a key of its own, which only the storefront root produces. Undefined for a variant that names no
 * page at all, which holds none.
 */
export const toPickPageMatchKey = ({
  baseID,
  url,
}: {
  baseID?: string | null;
  url?: string | null;
}): string | undefined => {
  if (baseID) return baseID;
  if (!url) return undefined;

  return toPickPageMatchPath(url) === PICK_PAGE_HOME_PATH ? PICK_PAGE_HOME_ID : undefined;
};

/**
 * The page a variant of the experiment being edited holds: a price test names it by the product it
 * prices, a URL test by the destination it sends traffic to. Undefined while the variant holds none,
 * which is the state a draft's arms start in.
 */
export const toVariantPageKey = (variant: Variant): string | undefined => {
  if (variant.price?.baseID) return variant.price.baseID;

  return toPickPageMatchKey({ baseID: variant.url?.baseID, url: variant.url?.redirectURL });
};

const toCampaignTarget = ({
  campaign,
  variant,
}: {
  campaign: ICampaignVariantTarget;
  variant: IVariantTarget;
}): IPickPageCampaignTarget => {
  const baseIds = new Set<string>();
  //Price
  if (variant.baseID) baseIds.add(variant.baseID);

  //Template
  campaign.targetPageIDs.forEach((targetPageId) => {
    const baseId = getShopifyPageId({ type: TemplateType.PRODUCT, id: targetPageId });
    if (baseId) baseIds.add(baseId);
  });

  return {
    campaignId: campaign.campaignID,
    campaignName: campaign.campaignName,
    campaignType: campaign.campaignType,
    group: campaign.campaignGroup
      ? { id: campaign.campaignGroup.id, title: campaign.campaignGroup.title }
      : undefined,
    baseIds,
    targetPageIds: campaign.targetPageIDs,
    destinationKey: toPickPageMatchKey({ baseID: variant.baseID, url: variant.redirectURL }),
    templateSuffix: variant.templateSuffix ?? undefined,
    isOrigin: variant.isOrigin,
    status: campaign.campaignStatus,
    priceId: variant.variantType?.kind === 'PRICE' ? variant.variantType.id : undefined,
    urlId: variant.variantType?.kind === 'URL' ? variant.variantType.id : undefined,
  };
};

export const toOtherCampaignTargets = ({
  campaignVariantTargets,
  excludedCampaignId,
}: {
  campaignVariantTargets: ICampaignVariantTarget[];
  excludedCampaignId?: string;
}): IPickPageOtherCampaignTargets => {
  const templateTargetsBySuffix: Record<string, IPickPageCampaignTarget[]> = {};
  const priceTargetByBaseId: Record<string, IPickPageCampaignTarget> = {};
  const urlTargetByDestination: Record<string, IPickPageCampaignTarget> = {};

  campaignVariantTargets.forEach((campaign) => {
    if (campaign.campaignID === excludedCampaignId) return;

    const isPriceCampaign = campaign.campaignType === CAMPAIGN_TYPE.PRICE;
    const isUrlCampaign = campaign.campaignType === CAMPAIGN_TYPE.URL;

    campaign.variants.forEach((variant) => {
      const campaignTarget = toCampaignTarget({ campaign, variant });

      if (isPriceCampaign) {
        campaignTarget.baseIds.forEach((baseId) => {
          if (!priceTargetByBaseId[baseId]) priceTargetByBaseId[baseId] = campaignTarget;
        });
        return;
      }

      // A URL test holds the page its variant points at, whatever kind of page that is — the same way
      // a price test locks a product. Keyed off the variant's own destination only, never the
      // campaign's target pages: those are ids of whatever page type the campaign targets, and
      // reading them as products would lock a product that happens to share their number.
      if (isUrlCampaign) {
        const { destinationKey } = campaignTarget;
        if (destinationKey && !urlTargetByDestination[destinationKey]) {
          urlTargetByDestination[destinationKey] = campaignTarget;
        }

        return;
      }

      if (!variant.templateSuffix) return;
      (templateTargetsBySuffix[variant.templateSuffix] ??= []).push(campaignTarget);
    });
  });

  return { templateTargetsBySuffix, priceTargetByBaseId, urlTargetByDestination };
};

export interface IIsPickPageSelectableParams extends IIsPickPageOnlineStoreAvailableParams {
  currentCampaignUsageByPageKey: Record<string, IPickPageCurrentCampaignUsage>;
  otherCampaignTargets: IPickPageOtherCampaignTargets;
}

/** The other experiment already testing this page, if any. */
export const findCampaignTargetByPage = ({
  page,
  otherCampaignTargets,
}: {
  page: IPickPageItem;
  otherCampaignTargets: IPickPageOtherCampaignTargets;
}): IPickPageCampaignTarget | undefined => {
  const priceTarget = otherCampaignTargets.priceTargetByBaseId[page.baseID];
  if (priceTarget) return priceTarget;

  const destinationKey = toPickPageMatchKey({ baseID: page.baseID, url: page.url });
  const urlTarget = destinationKey
    ? otherCampaignTargets.urlTargetByDestination[destinationKey]
    : undefined;
  if (urlTarget) return urlTarget;

  const targets = page.templateSuffix
    ? otherCampaignTargets.templateTargetsBySuffix[page.templateSuffix]
    : undefined;

  return targets?.[0];
};

/**
 * Whether a row can be picked at all — the same three reasons `usePickPageItemState` disables one,
 * as a single answer: the storefront has to serve the page, this experiment must not already test it,
 * and no other experiment may hold it.
 */
export const isPickPageSelectable = ({
  page,
  mode,
  isControl,
  currentCampaignUsageByPageKey,
  otherCampaignTargets,
}: IIsPickPageSelectableParams): boolean => {
  if (!isPickPageOnlineStoreAvailable({ page, mode, isControl })) return false;

  const pageKey = toPickPageMatchKey({ baseID: page.baseID, url: page.url });
  if (pageKey && currentCampaignUsageByPageKey[pageKey]) return false;

  return !findCampaignTargetByPage({ page, otherCampaignTargets });
};

/**
 * What a removal has to carry to hand this page back — a price test gives back its price record, a
 * URL test its destination record, a template test the template it locks. Undefined when the locking
 * campaign cannot hand it back, which keeps the removal shortcut off an action that would only fail.
 */
export const toRemoveConfirmState = ({
  campaignTarget,
  campaignId,
  campaignName,
}: {
  campaignTarget: IPickPageCampaignTarget;
  /** The group's id when the campaign is grouped, so the removal works off the same campaign the UI names. */
  campaignId: string;
  campaignName?: string;
}): IPickPageRemoveConfirmState | undefined => {
  const { priceId, urlId, templateSuffix } = campaignTarget;
  const base = { campaignId, campaignName };

  switch (campaignTarget.campaignType) {
    case CAMPAIGN_TYPE.PRICE:
      return priceId ? { ...base, campaignType: ICampaignType.PRICE, priceId } : undefined;

    case CAMPAIGN_TYPE.URL:
      return urlId ? { ...base, campaignType: ICampaignType.URL, urlId } : undefined;

    case CAMPAIGN_TYPE.TEMPLATE:
      return templateSuffix
        ? { ...base, campaignType: ICampaignType.TEMPLATE, templateKey: templateSuffix }
        : undefined;

    case CAMPAIGN_TYPE.TEMPLATE_BUNDLE:
      return templateSuffix
        ? { ...base, campaignType: ICampaignType.TEMPLATE_BUNDLE, templateKey: templateSuffix }
        : undefined;

    default:
      return undefined;
  }
};

/**
 * Pages already tested by the experiment being edited, keyed the way a listed row is. The variant the
 * modal was opened for is skipped here — its own page is filtered out of the list entirely (see
 * `PickPageProvider`) rather than flagged disabled through this map.
 */
export const toCurrentCampaignUsageByPageKey = ({
  campaign,
  targetVariantId,
}: {
  campaign?: Campaign;
  targetVariantId?: string;
}): Record<string, IPickPageCurrentCampaignUsage> => {
  const usageByPageKey: Record<string, IPickPageCurrentCampaignUsage> = {};

  campaign?.variants?.forEach((variant) => {
    if (!variant || variant.id === targetVariantId) return;

    const pageKey = toVariantPageKey(variant);
    if (!pageKey) return;

    usageByPageKey[pageKey] = { isControl: !!variant.isOrigin };
  });

  return usageByPageKey;
};
